/* global store */
/* global IS_PREVIEW_PAGE */
import { setTargetComponentTimeline } from 'Renderer/domOperations/animationOperations';
import {
  fireSwitchArtboardTrackingEvent,
  fireExpandTrackingEvent,
  fireCloseExpandTrackingEvent,
  fireClickTrackingEvent,
} from 'Tracking/actions/button';
import {
  fireCarouselSlideView,
} from 'Tracking/actions/smartgroup';
import { getScaledPixelValue, getScaledArtboardPixelValue, setArtboard, defaultArtboards, maybeChangeArtboard } from 'Renderer/domOperations/artboardOperations';
import { handleCarouselGroup } from 'Renderer/utils/smartGroupScript';
import { setRootDocument, setRootElement, resetTimeLine } from 'Renderer/actions/artboardActions';
import { showButtonSet, hideButtonSet } from 'Renderer/utils/buttonScript';
import gsap from 'gsap';
import { callVPAID } from 'Renderer/utils/vpaid';
import { AUTO_PLAY_VIDEO_CLASS } from 'Tracking/constants/selectors';

import { getSafeClassFromArray } from 'Render/utils';

const getElementAttribute = (el, attribute) => {
  if (el) return el.getAttribute(attribute);
  return null;
};

export const isVisible = (elem) => {
  const { rootElement } = store.getState(); // eslint-disable-line no-undef
  try {
    const tolerance = 1.2;
    const style = getComputedStyle(elem);
    if (style.display === 'none') return false;
    if (style.visibility !== 'visible') return false;
    if (style.opacity < 0.1) return false;
    if (elem.offsetWidth + elem.offsetHeight + elem.getBoundingClientRect().height +
      elem.getBoundingClientRect().width === 0) {
      return false;
    }
    const left = elem.getBoundingClientRect().left - rootElement.getBoundingClientRect().left;
    const top = elem.getBoundingClientRect().top - rootElement.getBoundingClientRect().top;
    const elemCenter = {
      x: (left + elem.offsetWidth) / 2,
      y: (top + elem.offsetHeight) / 2,
    };
    if (elemCenter.x < 0) return false;
    if (elemCenter.x > (rootElement.clientWidth * tolerance)) return false;
    if (elemCenter.y < 0) return false;
    if (elemCenter.y > (rootElement.clientHeight * tolerance)) return false;
    return true;
  } catch (e) {
    return false;
  }
};

export const isInCarousel = evt => evt.closest('[data-behaviour=carousel]') !== null;
export const isInActiveCarousel = evt => evt.closest('[data-behaviour=carousel]') !== null;
const isInMultiSkin = evt => evt.closest('.ivo-ad.skin.multi') !== null;

export const getContextElement = (evt = null) => {
  const { rootElement } = store.getState();
  if (evt) {
    const elem = ('nodeType' in evt) ? evt : evt.target;
    if (isInActiveCarousel(elem)) return elem.closest('[data-behaviour=carousel]');
    if (isInMultiSkin(elem)) return elem.closest('.ivo-ad.skin.multi');
  }
  return rootElement;
};

const getTargetElement = (evt, targetComponent) => {
  const { rootElement } = store.getState();
  const contextElement = getContextElement(evt);
  return contextElement.querySelector(`.${targetComponent}`) || rootElement.querySelector(`.${targetComponent}`);
};

export const extractTargetComponents = (evt, targetsComponents, component = '') => {
  const contextElement = getContextElement(evt);
  const targetComponentsArray = [];
  const components = Array.isArray(targetsComponents) ? targetsComponents : targetsComponents.split(',');
  [...components].forEach((targetComponent) => {
    const targetNodes = contextElement.querySelectorAll(`.${targetComponent} ${component}`);
    [...targetNodes].forEach((targetNode) => {
      targetComponentsArray.push(targetNode);
    });
  });
  return targetComponentsArray;
};

export const animateTransition = (component, transition, action) => {
  const transitionAlpha = (action === 'show') ? 1 : 0;
  const autoAlpha = { autoAlpha: transitionAlpha };
  const tween = [];
  if (!transition || transition === 'none') { return gsap.to(component, { x: 0, y: 0, autoAlpha: transitionAlpha, duration: 0 }); }
  if (transition.includes('fade')) {
    if (transition.includes('fast')) tween.push({ x: 0, y: 0, duration: '0.2' });
    if (transition.includes('slow')) tween.push({ x: 0, y: 0, duration: '0.6' });
    gsap.to(component, { ...tween[0], ...autoAlpha });
  }
  if (transition.includes('slide')) {
    const sharedAttributes = { duration: 0.4 };
    if (transition.includes('-in-')) {
      const endState = { x: 0, y: 0 };
      if (transition.includes('left')) tween.push({ x: 60, y: 0 });
      if (transition.includes('right')) tween.push({ x: +60, y: 0 });
      if (transition.includes('up')) tween.push({ y: +60, x: 0 });
      if (transition.includes('down')) tween.push({ y: -60, x: 0 });
      gsap.fromTo(component, { ...tween[0] }, { ...autoAlpha, ...endState, ...sharedAttributes });
    }
    if (transition.includes('-out-')) {
      if (transition.includes('left')) tween.push({ x: -60, y: 0 });
      if (transition.includes('right')) tween.push({ x: +60, y: 0 });
      if (transition.includes('up')) tween.push({ y: -60, x: 0 });
      if (transition.includes('down')) tween.push({ y: +60, x: 0 });
      gsap.to(component, { ...tween[0], ...autoAlpha, ...sharedAttributes });
    }
  }
};

export const handleExpand = (evt) => {
  const { rootElement, activeArtboard, artboards, baseElement } = store.getState();
  const thisWindow = window;
  const frame = thisWindow.frameElement;
  if (frame) frame.parentNode.style.maxHeight = 'none';
  fireExpandTrackingEvent(evt);
  baseElement.setAttribute('data-state', 'expanded');
  rootElement.setAttribute('data-ref-artboard', activeArtboard.name);
  const expandTarget = getElementAttribute(evt.target, 'data-expand-target');
  const targetArtboard = expandTarget ? artboards.filter(a => a.name === expandTarget)[0] : null;
  if (targetArtboard) setTimeout(setArtboard, 10, targetArtboard, 'expand');
};

export const handleClose = (evt) => {
  const { rootElement, artboards, canAccessIframe, overlayContainer, baseDocument, baseElement, isIframe } = store.getState();
  const dataState = getElementAttribute(baseElement, 'data-state');
  if (dataState === 'overlay' && canAccessIframe) {
    fireCloseExpandTrackingEvent(evt);
    baseElement.removeAttribute('data-state');
    window.top.document.body.style.overflow = 'visible';
    gsap.to(overlayContainer, 0.25, { autoAlpha: 0 });
    setRootDocument(baseDocument);
    setRootElement(baseElement);
    resetTimeLine();
    maybeChangeArtboard();
  } else if (dataState === 'expanded' && (!IS_PREVIEW_PAGE || isIframe)) {
    fireCloseExpandTrackingEvent(evt);
    baseElement.removeAttribute('data-state');
    const dataArtboard = artboards.filter(a => a.name === getElementAttribute(rootElement, 'data-ref-artboard'))[0];
    rootElement.removeAttribute('data-ref-artboard');
    const targetArtboard = dataArtboard || defaultArtboards(artboards)[0];
    setArtboard(targetArtboard, 'expand-close');
  } else {
    fireClickTrackingEvent(evt, 'click');
    const dataArtboard = artboards.filter(a => a.name === getElementAttribute(rootElement, 'data-ref-artboard'))[0];
    rootElement.removeAttribute('data-ref-artboard');
    const targetArtboard = dataArtboard || defaultArtboards(artboards)[0];
    setArtboard(targetArtboard, 'close');
  }
};

export const handleLaunchLightbox = (evt) => {
  const {
    rootElement,
    baseElement,
    canAccessIframe,
    overlayContainer,
    overlayElement,
    artboards,
    activeArtboard,
  } = store.getState();
  const updatedRootDocument = window.top.document;
  const updatedRootElement = overlayElement;
  const eventType = getElementAttribute(evt.target, 'data-event');

  fireExpandTrackingEvent(evt);
  window.top.document.body.style.overflow = 'hidden';

  if (canAccessIframe) {
    baseElement.setAttribute('data-state', 'overlay');
    gsap.to(overlayContainer, 0.25, { autoAlpha: 1 });
    setRootDocument(updatedRootDocument);
    setRootElement(updatedRootElement);
    updatedRootElement.style.width = '100%';
    updatedRootElement.style.height = '100%';
    resetTimeLine();
    maybeChangeArtboard();
  } else {
    rootElement.setAttribute('data-ref-artboard', activeArtboard.name);
    const targetArtboard = artboards.find(a => a.name === evt.target.getAttribute('data-lightbox-target'));
    setArtboard(targetArtboard, eventType);
  }
};

export const handleSwitchArtboard = (evt, dataTarget) => {
  const { artboards } = store.getState();
  const eventTarget = evt ? getElementAttribute(evt.target, 'data-switchartboard-target') || '' : null;
  const target = eventTarget || dataTarget;
  const dataArtboard = artboards.find(a => a.name === target);
  const targetArtboard = dataArtboard || defaultArtboards(artboards)[0];
  const eventType = 'switch-artboard';
  if (evt) fireSwitchArtboardTrackingEvent(evt, targetArtboard.name);
  setTimeout(setArtboard, 10, targetArtboard, eventType);
};

export const handleSwipeLeftSwitchArtboard = (evt, dataTarget) => {
  const target = evt ? getElementAttribute(evt.target, 'data-swipeleft-switchartboard-target') : dataTarget;
  handleSwitchArtboard(evt, target);
};

export const handleSwipeRightSwitchArtboard = (evt, dataTarget) => {
  const target = evt ? getElementAttribute(evt.target, 'data-swiperight-switchartboard-target') : dataTarget;
  handleSwitchArtboard(evt, target);
};

export const handleSwipeUpSwitchArtboard = (evt, dataTarget) => {
  const target = evt ? getElementAttribute(evt.target, 'data-swipeup-switchartboard-target') : dataTarget;
  handleSwitchArtboard(evt, target);
};

export const handleSwipeDownSwitchArtboard = (evt, dataTarget) => {
  const target = evt ? getElementAttribute(evt.target, 'data-swipedown-switchartboard-target') : dataTarget;
  handleSwitchArtboard(evt, target);
};

export const showComponents = (evt, targetComponents, transitionEffect) => {
  [...targetComponents].forEach((targetComponent) => {
    const showComponentObject = getTargetElement(evt, targetComponent);
    if (!showComponentObject) return;
    const componentTimeline = setTargetComponentTimeline(showComponentObject);
    if (componentTimeline) componentTimeline.play(0);
    animateTransition(showComponentObject, transitionEffect, 'show');
    [...showComponentObject.querySelectorAll('.i-v-autoplay')].forEach((v) => {
      v.currentTime = 0;
      v.play();
    });
    const dataBehaviour = getElementAttribute(showComponentObject, 'data-behaviour');
    if (dataBehaviour && dataBehaviour === 'carousel') handleCarouselGroup(showComponentObject);
  });
};

export const hideComponents = (evt, targetComponents, transitionEffect) => {
  [...targetComponents].forEach((targetComponent) => {
    const hideComponentObject = getTargetElement(evt, targetComponent);
    if (!hideComponentObject) return;
    animateTransition(hideComponentObject, transitionEffect, 'hide');
    [...hideComponentObject.querySelectorAll('video')].forEach((v) => {
      v.pause();
    });
  });
};

export const handleShowComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'click');
  const targetComponents = evt ? evt.target.getAttribute('data-show-target').split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute('data-show-transition') : null;
  showComponents(evt, targetComponents, transitionEffect);
};

export const handleHideComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'click');
  const targetComponents = evt ? evt.target.getAttribute('data-hide-target').split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute('data-hide-transition') : null;
  hideComponents(evt, targetComponents, transitionEffect);
};

export const handleMouseOverShowComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'mouseover');
  const evtTargets = evt ? getElementAttribute(evt.target, 'data-mouseover-show-target') || '' : '';
  const targetComponents = evt ? evtTargets.split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute('data-mouseover-show-transition') : null;
  showComponents(evt, targetComponents, transitionEffect);
};

export const handleMouseOverHideComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'mouseover');
  const evtTargets = evt ? getElementAttribute(evt.target, 'data-mouseover-hide-target') || '' : '';
  const targetComponents = evt ? evtTargets.split(',') : target;
  const transitionEffect = evt ? getElementAttribute(evt.target, 'data-mouseover-hide-transition') : null;
  hideComponents(evt, targetComponents, transitionEffect);
};

export const handleMouseOutShowComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'mouseout');
  const evtTargets = evt ? getElementAttribute(evt.target, 'data-mouseover-hide-target') || '' : '';
  const targetComponents = evt ? evtTargets.split(',') : target;
  const transitionEffect = evt ? getElementAttribute(evt.target, 'data-mouseout-show-transition') : null;
  showComponents(evt, targetComponents, transitionEffect);
};

export const handleMouseOutHideComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'mouseout');
  const evtTargets = evt ? getElementAttribute(evt.target, 'data-mouseout-hide-target') || '' : '';
  const targetComponents = evt ? evtTargets.split(',') : target;
  const transitionEffect = evt ? getElementAttribute(evt.target, 'data-mouseout-hide-transition') : null;
  hideComponents(evt, targetComponents, transitionEffect);
};

export const handleSwipeShowComponent = (evt, direction, target) => {
  if (evt) fireClickTrackingEvent(evt, `swipe-${direction}`);
  const evtTargets = evt ? getElementAttribute(evt.target, `data-swipe${direction}-show-target`) || '' : '';
  const targetComponents = evt ? evtTargets.split(',') : target;
  const transitionEffect = evt ? getElementAttribute(evt.target, `data-swipe${direction}-show-transition`) : null;
  showComponents(evt, targetComponents, transitionEffect);
};

export const handleSwipeHideComponent = (evt, direction, target) => {
  if (evt) fireClickTrackingEvent(evt, `swipe-${direction}`);
  const evtTargets = evt ? getElementAttribute(evt.target, `data-swipe${direction}-hide-target`) || '' : '';
  const targetComponents = evt ? evtTargets.split(',') : target;
  const transitionEffect = evt ? getElementAttribute(evt.target, `data-swipe${direction}-hide-transition`) : null;
  hideComponents(evt, targetComponents, transitionEffect);
};

export const handleSwipeLeftShowComponent = evt => handleSwipeShowComponent(evt, 'left');
export const handleSwipeRightShowComponent = evt => handleSwipeShowComponent(evt, 'right');
export const handleSwipeUpShowComponent = evt => handleSwipeShowComponent(evt, 'up');
export const handleSwipeDownShowComponent = evt => handleSwipeShowComponent(evt, 'down');

export const handleSwipeLeftHideComponent = evt => handleSwipeHideComponent(evt, 'left');
export const handleSwipeRightHideComponent = evt => handleSwipeHideComponent(evt, 'right');
export const handleSwipeUpHideComponent = evt => handleSwipeHideComponent(evt, 'up');
export const handleSwipeDownHideComponent = evt => handleSwipeHideComponent(evt, 'down');

const getRemoteContainers = () => {
  const adWrappers = [];
  [...window.top.document.querySelectorAll('#i-a-wrapper')].forEach(adWrapper => adWrappers.push(adWrapper));
  [...window.top.document.querySelectorAll('iframe[data-ivo-frame]')].forEach((adFrame) => {
    try {
      if (adFrame.document === document) return;
      [...adFrame.contentWindow.document.querySelectorAll('#i-a-wrapper')].forEach(adWrapper => adWrappers.push(adWrapper));
    } catch (e) {
      console.g('ivo ::: cannot access frame contents');
    }
  });
  return adWrappers;
};

export const hideRemoteComponents = (targetComponents, transitionEffect) => {
  const adWrappers = getRemoteContainers();
  [...adWrappers].forEach((wrapper) => {
    [...targetComponents].forEach((targetComponent) => {

      const targetComponentObject = wrapper.querySelector(`.${targetComponent}`);
      if (targetComponentObject) {
        animateTransition(targetComponentObject, transitionEffect, 'hide');
        [...targetComponentObject.querySelectorAll('video')].forEach((v) => {
          v.pause();
        });
      }
    });
  });
};

export const showRemoteComponents = (targetComponents, transitionEffect) => {
  const { rootElement } = store.getState();
  const currentRootElement = rootElement;
  const adWrappers = getRemoteContainers();
  [...adWrappers].forEach((wrapper) => {
    [...targetComponents].forEach((targetComponent) => {
      const targetComponentObject = wrapper.querySelector(`.${targetComponent}`);
      if (targetComponentObject) {
        const dataFrame = IS_PREVIEW_PAGE ? targetComponentObject.ownerDocument.defaultView.frameElement : null;
        const dataElement = (IS_PREVIEW_PAGE && dataFrame) ? dataFrame.closest('[data-preview-artboard]') : targetComponentObject.closest('[data-artboard]');
        const componentArtboard = IS_PREVIEW_PAGE ? dataElement.getAttribute('data-preview-artboard') : dataElement.getAttribute('data-artboard');
        const updatedRootDocument = targetComponentObject.ownerDocument;
        setRootDocument(updatedRootDocument);
        setRootElement(targetComponentObject.parentNode);
        const componentTimeline = setTargetComponentTimeline(targetComponentObject, componentArtboard, wrapper);
        if (componentTimeline) componentTimeline.play(0);
        setRootElement(currentRootElement);
        animateTransition(targetComponentObject, transitionEffect, 'show');
        [...targetComponentObject.querySelectorAll('.i-v-autoplay')].forEach((v) => {
          v.currentTime = 0;
          v.play();
        });
      }
    });
  });
};

export const handleShowRemoteComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'click');
  const targetComponents = evt ? evt.target.getAttribute('data-show-remote-target').split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute('data-show-remote-transition') : target;
  showRemoteComponents(targetComponents, transitionEffect);
};

export const handleHideRemoteComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'click');
  const targetComponents = evt ? evt.target.getAttribute('data-hide-remote-target').split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute('data-hide-remote-transition') : target;
  hideRemoteComponents(targetComponents, transitionEffect);
};

export const handleMouseOverShowRemoteComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'mouseover');
  const targetComponents = evt ? evt.target.getAttribute('data-mouseover-show-remote-target').split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute('data-mouseover-show-remote-transition') : target;
  showRemoteComponents(targetComponents, transitionEffect);
};

export const handleMouseOverHideRemoteComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'mouseover');
  const targetComponents = evt ? evt.target.getAttribute('data-mouseover-hide-remote-target').split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute('data-mouseover-hide-remote-transition') : target;
  hideRemoteComponents(targetComponents, transitionEffect);
};

export const handleMouseOutShowRemoteComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'mouseout');
  const targetComponents = evt ? evt.target.getAttribute('data-mouseout-show-remote-target').split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute('data-mouseout-show-remote-transition') : target;
  showRemoteComponents(targetComponents, transitionEffect);
};

export const handleMouseOutHideRemoteComponent = (evt, target) => {
  if (evt) fireClickTrackingEvent(evt, 'mouseout');
  const targetComponents = evt ? evt.target.getAttribute('data-mouseout-hide-remote-target').split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute('data-mouseout-hide-remote-transition') : target;
  hideRemoteComponents(targetComponents, transitionEffect);
};

export const handleSwipeShowRemoteComponent = (evt, direction, target) => {
  if (evt) fireClickTrackingEvent(evt, `swipe-${direction}`);
  const targetComponents = evt ? evt.target.getAttribute(`data-swipe${direction}-show-remote-target`).split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute(`data-swipe${direction}-show-remote-transition`) : target;
  showRemoteComponents(targetComponents, transitionEffect);
};

export const handleSwipeHideRemoteComponent = (evt, direction, target) => {
  if (evt) fireClickTrackingEvent(evt, `swipe-${direction}`);
  const targetComponents = evt ? evt.target.getAttribute(`data-swipe${direction}-hide-remote-target`).split(',') : target;
  const transitionEffect = evt ? evt.target.getAttribute(`data-swipe${direction}-hide-remote-transition`) : target;
  hideRemoteComponents(targetComponents, transitionEffect);
};

export const handleSwipeLeftShowRemoteComponent = evt => handleSwipeShowRemoteComponent(evt, 'left');
export const handleSwipeRightShowRemoteComponent = evt => handleSwipeShowRemoteComponent(evt, 'right');
export const handleSwipeUpShowRemoteComponent = evt => handleSwipeShowRemoteComponent(evt, 'up');
export const handleSwipeDownShowRemoteComponent = evt => handleSwipeShowRemoteComponent(evt, 'down');

export const handleSwipeLeftHideRemoteComponent = evt => handleSwipeHideRemoteComponent(evt, 'left');
export const handleSwipeRightHideRemoteComponent = evt => handleSwipeHideRemoteComponent(evt, 'right');
export const handleSwipeUpHideRemoteComponent = evt => handleSwipeHideRemoteComponent(evt, 'up');
export const handleSwipeDownHideRemoteComponent = evt => handleSwipeHideRemoteComponent(evt, 'down');

const getScrollableDimensions = (group) => {
  const maxDimensions = [];
  maxDimensions.width = 0;
  maxDimensions.height = 0;
  [...group.childNodes].forEach((el) => {
    const elWidth = el.clientWidth;
    const elHeight = el.clientHeight;
    if (elWidth > maxDimensions.width) maxDimensions.width = elWidth;
    if (elHeight > maxDimensions.height) maxDimensions.height = elHeight;
  });
  return maxDimensions;
};

const updateActiveSlide = (activeSlide, shadowSlide) => {
  activeSlide.removeAttribute('data-active');
  activeSlide.setAttribute('data-shadow', 'true');
  shadowSlide.removeAttribute('data-shadow');
  shadowSlide.setAttribute('data-active', 'true');
};

export const controlCarousel = (group, direction, amount) => {
  const { activeArtboard, rootElement } = store.getState();

  const isSkin = (isInMultiSkin(group));
  const contextElement = isSkin ? group.closest('.ivo-ad.skin') : rootElement;
  const widthAttribute = contextElement.getAttribute('data-max-width') || contextElement.getAttribute('data-width');
  const artboardWidth = isSkin ? parseInt(widthAttribute, 10) : activeArtboard.dimensions.width;
  const looping = (group.childNodes[1].getAttribute('data-repeat') === 'true') || false;
  const isUp = ['down', 'right'].indexOf(direction) >= 0;
  const isDown = ['up', 'left'].indexOf(direction) >= 0;
  const isVertical = ['up', 'down'].indexOf(direction) >= 0;

  const scrollableNode = group.childNodes[1];

  const scrollableDimensions = getScrollableDimensions(group);

  const scaledArtboard = activeArtboard.dimensions.width !== contextElement.getBoundingClientRect().width;

  const scalePercentage = scaledArtboard ? (contextElement.getBoundingClientRect().width / artboardWidth) : 1;

  let adjustedScroll = amount * scalePercentage;

  const vector = isVertical ? 'y' : 'x';
  const operator = isUp ? '-' : '+';
  const containerGeom = isVertical ? scrollableDimensions.height : scrollableDimensions.width;
  const groupGeom = isVertical ? scrollableNode.scrollHeight : scrollableNode.scrollWidth;
  const groupOffset = groupGeom - containerGeom;

  const tween = [];
  const sharedAttributes = [];
  sharedAttributes.duration = 0.4;
  sharedAttributes.ease = 'circ.out';

  const groupStart = gsap.getProperty(group.parentNode, vector);

  const activeSlide = group.querySelector('div[data-active]');
  const shadowSlide = group.querySelector('div[data-shadow]');

  const currentTweens = gsap.getTweensOf(activeSlide);
  if (currentTweens.length) {
    if (currentTweens[0].isActive()) return;
  }

  if (looping) {
    const activeSlideComp = activeSlide.getBoundingClientRect();
    const shadowSlideComp = shadowSlide.getBoundingClientRect();
    const matchedGeom = (activeSlideComp[vector] === shadowSlideComp[vector]);
    if (operator === '+') {
      const activeSlideGeom = gsap.getProperty(activeSlide, vector) + adjustedScroll;
      if (matchedGeom || (activeSlideGeom >= groupStart)) {
        const shadowTween = [];
        shadowTween[vector] = ((activeSlideGeom - groupGeom) - adjustedScroll);
        gsap.to(shadowSlide, 0, { ...shadowTween });
        updateActiveSlide(activeSlide, shadowSlide);
      }
      const slideNo = Math.round(activeSlideGeom / containerGeom);
      fireCarouselSlideView(activeSlide, slideNo);
    } else if (operator === '-') {
      const activeSlideGeom = (gsap.getProperty(activeSlide, vector) - adjustedScroll) + groupGeom;
      if (matchedGeom || (activeSlideGeom <= (groupStart + containerGeom))) {
        const shadowTween = [];
        shadowTween[vector] = (gsap.getProperty(activeSlide, vector) + groupGeom);
        gsap.to(shadowSlide, 0, { ...shadowTween });
        updateActiveSlide(activeSlide, shadowSlide);
      }
      const slideNo = Math.round(activeSlideGeom / containerGeom);
      fireCarouselSlideView(activeSlide, slideNo);
    }
  } else {
    const preScrollPosition = gsap.getProperty(group.childNodes[1], vector);
    const preScrollGeom = (preScrollPosition + groupGeom);
    const scrollCalc = (preScrollPosition + groupGeom) - adjustedScroll;

    const slideNo = Math.round(scrollCalc / containerGeom);
    fireCarouselSlideView(activeSlide, slideNo);

    if (isUp && (scrollCalc < containerGeom)) {
      const adjustment = (containerGeom - groupGeom) - preScrollPosition;
      adjustedScroll = Math.abs(adjustment);
      group.setAttribute('data-autoplay-complete', '');
    } else if (isDown && (preScrollPosition + adjustedScroll) >= 0) {
      adjustedScroll = (0 + Math.abs(preScrollPosition));
      group.setAttribute('data-autoplay-complete', '');
    }
  }

  tween[vector] = `${operator}=${adjustedScroll}`;
  gsap.to([group.childNodes[1], group.childNodes[2]], {
    ...tween,
    ...sharedAttributes,
    onStart: () => {
      const nestedGroups = activeSlide.querySelectorAll('[data-group]');
      [...nestedGroups].forEach((nestedGroup) => {
        const groupPosition = isVertical ? group.getBoundingClientRect().top : group.getBoundingClientRect().left;
        const currentPosition = isVertical ? nestedGroup.getBoundingClientRect().top : nestedGroup.getBoundingClientRect().left;
        const calcLeft = eval(`${currentPosition}${operator}${amount}`) - groupPosition;
        const calcPosition = calcLeft + (nestedGroup.getBoundingClientRect().width / 2);
        if ((calcPosition > 0) && (calcPosition < amount)) {
          const componentTimeline = setTargetComponentTimeline(nestedGroup);
          if (componentTimeline) componentTimeline.play(0);
          [...nestedGroup.querySelectorAll(`.${AUTO_PLAY_VIDEO_CLASS}`)].forEach(v => v.play());
        }
      });
    },
  });
};

export const handleScroll = (evt, target) => {
  const { sessionId } = store.getState();
  const contextElement = getContextElement(evt);

  if (evt) fireClickTrackingEvent(evt, 'click');
  const scrollTarget = evt ? evt.target.getAttribute('data-carousel-target') : target;
  const scrollObject = contextElement.querySelector(`.${scrollTarget}`);
  const behaviour = evt ? scrollObject.querySelector('div[data-behaviour]').getAttribute('data-behaviour') : '';
  if (behaviour !== 'carousel') return;
  const scrollDirection = evt ? evt.target.getAttribute('data-carousel-direction') : 'down';
  const scrollAmount = evt ? evt.target.getAttribute('data-carousel-amount') : 0;

  scrollObject.setAttribute('data-interacted', 'true');
  if (window[`ivoCarouselEvents-${sessionId}`][scrollObject.id]) clearInterval(window[`ivoCarouselEvents-${sessionId}`][scrollObject.id]);

  controlCarousel(scrollObject, scrollDirection, scrollAmount);
};

export const videoControlCarousel = (evt) => {
  const { rootElement } = store.getState();
  const scrollTarget = evt.target.getAttribute('data-carousel-target');
  const scrollObject = rootElement.querySelector(`.${scrollTarget}`);
  const scrollDirection = evt.target.getAttribute('data-carousel-direction') || 'down';
  const scrollAmount = evt.target.getAttribute('data-carousel-amount') || 0;

  scrollObject.setAttribute('data-interacted', 'true');
  controlCarousel(scrollObject, scrollDirection, scrollAmount);
};

export const handleControlCarousel = (evt, direction) => {
  const { rootElement } = store.getState();
  if (evt) fireClickTrackingEvent(evt, `swipe-${direction}`);
  const scrollTarget = evt.target.getAttribute(`data-swipe${direction}-carousel-target`);
  const scrollObject = rootElement.querySelector(`.${scrollTarget}`);
  const behaviour = evt ? scrollObject.querySelector('div[data-behaviour]').getAttribute('data-behaviour') : '';
  if (behaviour !== 'carousel') return;
  const scrollDirection = evt ? evt.target.getAttribute(`data-swipe${direction}-carousel-direction`) : 'down';
  const scrollAmount = evt ? evt.target.getAttribute(`data-swipe${direction}-carousel-amount`) : 0;
  scrollObject.setAttribute('data-interacted', 'true');
  controlCarousel(scrollObject, scrollDirection, scrollAmount);
};

export const handleSwipeLeftCarousel = evt => (handleControlCarousel(evt, 'left'));
export const handleSwipeRightCarousel = evt => (handleControlCarousel(evt, 'right'));
export const handleSwipeUpCarousel = evt => (handleControlCarousel(evt, 'up'));
export const handleSwipeDownCarousel = evt => (handleControlCarousel(evt, 'down'));

export const handlePlayVideo = (evt, eventPrefix = '', target = '') => {
  const contextElement = getContextElement(evt);
  if (evt) fireClickTrackingEvent(evt, 'click');
  const targetComponent = evt ? evt.target.getAttribute(`data-${eventPrefix}video-play-target`) : target;
  if (evt) hideButtonSet(evt.target, 'play');
  const videos = contextElement.querySelectorAll(`.${targetComponent} video`);
  [...videos].forEach((video) => {
    if (isVisible(video)) {
      video.play();
      const pauseButton = contextElement.querySelector(`[data-${eventPrefix}video-pause-target='${targetComponent}']`);
      if (pauseButton) { showButtonSet(pauseButton, 'pause'); }
      if (video.hasAttribute('data-endframe')) {
        const endFrame = video.getAttribute('data-endframe');
        const endFrameComponent = contextElement.querySelector(`.${endFrame}`);
        if (endFrameComponent) { endFrameComponent.style.display = 'none'; }
      }
    } else {
      const { vpaid } = store.getState();
      const vpaidComponent = contextElement.querySelector('#i-a-wrapper').getAttribute('data-vaid-component');
      if (vpaid && (targetComponent === vpaidComponent)) callVPAID().resumeAd();
    }
  });
};

export const handleVideoEndPlayVideo = (evt) => {
  setTimeout(() => {
    handlePlayVideo(evt);
  }, 100);
};

export const handleReplayVideo = (evt, eventPrefix = '') => {
  fireClickTrackingEvent(evt, 'click');
  const contextElement = getContextElement(evt);
  const targetComponent = evt.target.getAttribute(`data-${eventPrefix}video-replay-target`);
  hideButtonSet(evt.target, 'replay');
  const videos = extractTargetComponents(evt, targetComponent, 'video');
  [...videos].forEach((video) => {
    if (isVisible(video)) {
      video.muted = false;
      video.play();
    }
    const unmuteButton = contextElement.querySelector(`[data-${eventPrefix}video-unmute-target='${targetComponent}']`);
    if (unmuteButton) { hideButtonSet(unmuteButton, 'unmute'); }
    const muteButton = contextElement.querySelector(`[data-${eventPrefix}video-mute-target='${targetComponent}']`);
    if (muteButton) { showButtonSet(muteButton, 'mute'); }
    const pauseButton = contextElement.querySelector(`[data-${eventPrefix}video-pause-target='${targetComponent}']`);
    if (pauseButton) { showButtonSet(pauseButton); }
    if (video.hasAttribute('data-endframe')) {
      const endFrame = video.getAttribute('data-endframe');
      const endFrameComponent = contextElement.querySelector(`.${endFrame}`);
      if (endFrameComponent) { endFrameComponent.style.display = 'none'; }
    }
  });
};

export const handlePauseVideo = (evt, eventPrefix = '', target) => {
  if (evt) fireClickTrackingEvent(evt, 'click');
  const contextElement = getContextElement(evt);
  const targetComponent = evt ? evt.target.getAttribute(`data-${eventPrefix}video-pause-target`) : target;
  if (evt) hideButtonSet(evt.target, 'pause');
  const videos = extractTargetComponents(evt, targetComponent, 'video');
  [...videos].forEach((video) => {
    if (video) {
      video.pause();
    } else {
      const { vpaid } = store.getState();
      const vpaidComponent = contextElement.querySelector('#i-a-wrapper').getAttribute('data-vaid-component');
      if (vpaid && (targetComponent === vpaidComponent)) callVPAID().pauseAd();
    }
    const playButton = contextElement.querySelector(`[data-${eventPrefix}video-play-target='${targetComponent}']`);
    if (playButton) { showButtonSet(playButton, 'play'); }
  });
};

export const handleMuteVideo = (evt, eventPrefix = '', target) => {
  if (evt) fireClickTrackingEvent(evt, 'click');
  const contextElement = getContextElement(evt);
  const targetComponent = evt ? evt.target.getAttribute(`data-${eventPrefix}video-mute-target`) : target;
  if (evt) hideButtonSet(evt.target, 'mute');
  const videos = extractTargetComponents(evt, targetComponent, 'video');
  [...videos].forEach((video) => {
    if (video) {
      video.muted = true;
    } else {
      const { vpaid } = store.getState();
      const vpaidComponent = contextElement.querySelector('#i-a-wrapper').getAttribute('data-vaid-component');
      if (vpaid && (targetComponent === vpaidComponent)) callVPAID().setAdVolume(0);
    }
    const unmuteButton = contextElement.querySelector(`[data-${eventPrefix}video-unmute-target='${targetComponent}']`);
    if (unmuteButton) { showButtonSet(unmuteButton, 'unmute'); }
  });
};

export const handleUnmuteVideo = (evt, eventPrefix = '', target) => {
  if (evt) fireClickTrackingEvent(evt, 'click');
  const contextElement = getContextElement(evt);
  const targetComponent = evt ? evt.target.getAttribute(`data-${eventPrefix}video-unmute-target`) : target;
  if (evt) hideButtonSet(evt.target, 'unmute');
  const videos = extractTargetComponents(evt, targetComponent, 'video');
  [...videos].forEach((video) => {
    if (video) {
      video.muted = false;
    } else {
      const { vpaid } = store.getState();
      const vpaidComponent = contextElement.querySelector('#i-a-wrapper').getAttribute('data-vaid-component');
      if (vpaid && (targetComponent === vpaidComponent)) callVPAID().setAdVolume(1);
    }
    const muteButton = contextElement.querySelector(`[data-${eventPrefix}video-mute-target='${targetComponent}']`);
    if (muteButton) { showButtonSet(muteButton, 'mute'); }
  });
};

export const handleControlSwipeGallery = (evt) => {
  fireClickTrackingEvent(evt, 'click');
  const { rootElement } = store.getState();
  const targetComponent = evt.target.getAttribute('data-swipegallery-target');
  const swipeDirection = evt.target.getAttribute('data-swipegallery-direction');
  if (swipeDirection === 'next') rootElement.querySelector(`.${targetComponent}`).next();
  if (swipeDirection === 'previous') rootElement.querySelector(`.${targetComponent}`).prev();
};

export const handleSwipeLeftPlayVideo = evt => (handlePlayVideo(evt, 'swipeleft-'));
export const handleSwipeLeftReplayVideo = evt => (handleReplayVideo(evt, 'swipeleft-'));
export const handleSwipeLeftPauseVideo = evt => (handlePauseVideo(evt, 'swipeleft-'));
export const handleSwipeLeftMuteVideo = evt => (handleMuteVideo(evt, 'swipeleft-'));
export const handleSwipeLeftUnmuteVideo = evt => (handleUnmuteVideo(evt, 'swipeleft-'));

export const handleSwipeRightPlayVideo = evt => (handlePlayVideo(evt, 'swiperight-'));
export const handleSwipeRightReplayVideo = evt => (handleReplayVideo(evt, 'swiperight-'));
export const handleSwipeRightPauseVideo = evt => (handlePauseVideo(evt, 'swiperight-'));
export const handleSwipeRightMuteVideo = evt => (handleMuteVideo(evt, 'swiperight-'));
export const handleSwipeRightUnmuteVideo = evt => (handleUnmuteVideo(evt, 'swiperight-'));

export const handleSwipeUpPlayVideo = evt => (handlePlayVideo(evt, 'swipeup-'));
export const handleSwipeUpReplayVideo = evt => (handleReplayVideo(evt, 'swipeup-'));
export const handleSwipeUpPauseVideo = evt => (handlePauseVideo(evt, 'swipeup-'));
export const handleSwipeUpMuteVideo = evt => (handleMuteVideo(evt, 'swipeup-'));
export const handleSwipeUpUnmuteVideo = evt => (handleUnmuteVideo(evt, 'swipeup-'));

export const handleSwipeDownPlayVideo = evt => (handlePlayVideo(evt, 'swipedown-'));
export const handleSwipeDownReplayVideo = evt => (handleReplayVideo(evt, 'swipedown-'));
export const handleSwipeDownPauseVideo = evt => (handlePauseVideo(evt, 'swipedown-'));
export const handleSwipeDownMuteVideo = evt => (handleMuteVideo(evt, 'swipedown-'));
export const handleSwipeDownUnmuteVideo = evt => (handleUnmuteVideo(evt, 'swipedown-'));

const controlTimeline = (timelines = null, type, position) => {
  const { activeArtboard, adNameSpace, rootElement, timeline } = store.getState();

  timelines.forEach((ttl) => {
    const elem = rootElement.querySelector(`.${ttl}`);
    const isMain = (ttl.toLowerCase().includes('main'));
    if (elem || isMain) {
      const tl = isMain ? timeline : window[adNameSpace].targetComponentTimelines[activeArtboard.key][elem.id];
      if (type === 'Play') tl.resume();
      if (type === 'Pause') tl.pause();
      if (type === 'PlayFrom') tl.play(position);
      if (type === 'PauseAt') tl.pause(position);
    }
  });
};

const keyframeControlTimeline = (actions) => {
  const timelines = actions.keyframeControlTimeline || [];
  const type = actions.keyframeControlTimelineType;
  const position = parseFloat(actions.keyframeControlTimelineSeconds, 10);
  const targetTimelines = timelines.map(ttl => getSafeClassFromArray(0, ttl));
  if (type) controlTimeline(targetTimelines, type, position);
};

export const handleButtonControlTimeline = (evt) => {
  const target = evt.target;
  const timelines = target.getAttribute('data-timeline-target').split(',');
  const type = target.getAttribute('data-timeline-type');
  const position = parseFloat(target.getAttribute('data-timeline-position'), 10);
  if (type) controlTimeline(timelines, type, position);
};

const keyframeShowComponent = (actions) => {
  const targetComponents = actions.keyframeShowComponent || [];
  const transitionEffect = actions.keyframeShowTransitionEffect;
  const safeClassTargetComponents = targetComponents.map(component => getSafeClassFromArray(0, component));
  showComponents(null, safeClassTargetComponents, transitionEffect);
};

const keyframeHideComponent = (actions) => {
  const targetComponents = actions.keyframeHideComponent || [];
  const transitionEffect = actions.keyframeHideTransitionEffect;
  const safeClassTargetComponents = targetComponents.map(component => getSafeClassFromArray(0, component));
  hideComponents(null, safeClassTargetComponents, transitionEffect);
};

const keyframeShowRemoteComponent = (actions) => {
  const targetComponents = actions.keyframeShowRemoteComponent || [];
  const transitionEffect = actions.keyframeShowRemoteTransitionEffect;
  const safeClassTargetComponents = targetComponents.map(component => getSafeClassFromArray(0, component));
  showRemoteComponents(safeClassTargetComponents, transitionEffect);
};

const keyframeHideRemoteComponent = (actions) => {
  const targetComponents = actions.keyframeHideRemoteComponent || [];
  const transitionEffect = actions.keyframeHideRemoteTransitionEffect;
  const safeClassTargetComponents = targetComponents.map(component => getSafeClassFromArray(0, component));
  hideRemoteComponents(safeClassTargetComponents, transitionEffect);
};

const keyframeSwitchArtboard = (actions) => {
  const artboard = actions.keyframeSwitchArtboardTarget;
  if (artboard) handleSwitchArtboard(null, artboard);
};

const keyframePlayVideo = (actions) => {
  const targetComponents = actions.keyframePlayVideoTarget || [];
  const safeClassTargetComponents = targetComponents.map(component => getSafeClassFromArray(0, component));
  handlePlayVideo(null, null, safeClassTargetComponents);
};

const keyframePauseVideo = (actions) => {
  const targetComponents = actions.keyframePlayVideoTarget || [];
  const safeClassTargetComponents = targetComponents.map(component => getSafeClassFromArray(0, component));
  handlePauseVideo(null, null, safeClassTargetComponents);
};

const keyframeMuteVideo = (actions) => {
  const targetComponents = actions.keyframeMuteVideoTarget || [];
  const safeClassTargetComponents = targetComponents.map(component => getSafeClassFromArray(0, component));
  handleMuteVideo(null, null, safeClassTargetComponents);
};

const keyframeUnmuteVideo = (actions) => {
  const targetComponents = actions.keyframeUnmuteVideoTarget || [];
  const safeClassTargetComponents = targetComponents.map(component => getSafeClassFromArray(0, component));
  handleUnmuteVideo(null, null, safeClassTargetComponents);
};

export const handleKeyFrameActions = (actions) => {
  if (!actions.keyFrameActions) return;
  if (actions.keyFrameActions.includes('show')) keyframeShowComponent(actions);
  if (actions.keyFrameActions.includes('hide')) keyframeHideComponent(actions);
  if (actions.keyFrameActions.includes('show-remote')) keyframeShowRemoteComponent(actions);
  if (actions.keyFrameActions.includes('hide-remote')) keyframeHideRemoteComponent(actions);
  if (actions.keyFrameActions.includes('play-video')) keyframePlayVideo(actions);
  if (actions.keyFrameActions.includes('pause-video')) keyframePauseVideo(actions);
  if (actions.keyFrameActions.includes('mute-video')) keyframeMuteVideo(actions);
  if (actions.keyFrameActions.includes('unmute-video')) keyframeUnmuteVideo(actions);
  if (actions.keyFrameActions.includes('switch-artboard')) keyframeSwitchArtboard(actions);
  if (actions.keyFrameActions.includes('control-timeline')) keyframeControlTimeline(actions);
};



// WEBPACK FOOTER //
// ./app/javascript/renderer/domOperations/actionOperations.js